import { useFormik } from "formik";
import { Col, Form, Row, Tab, Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import * as yup from "yup";
import Select from "react-select";
import {
  amountTypes,
  formatDate,
  formatForQty,
  waitFor,
} from "../../../utils/helpers";
import CurrencyCustomInput from "../../utils/CurrencyCustomInput";
import NoTableItem from "../../utils/NoTableItem";
import { useMemo, useState } from "react";
import currency from "currency.js";
import {
  addMonths,
  addQuarters,
  addYears,
  format,
  getDate,
  getDaysInMonth,
} from "date-fns";
import DateTime from "react-datetime";
import { addDays } from "date-fns";
import useDebounce from "../../../utils/hooks";
import { cloneDeep, isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import ConfirmDialog from "../../ConfirmDialogue";
import queryString from "query-string";
import { DeleteIcon, ExcelIcon, TrashIcon } from "../../Icons";
import ModalLoader from "../../utils/ModalLoader";
import CachedIcon from "mdi-react/CachedIcon";
import * as XLSX from "xlsx";

const EPSILON = 0.01;
const MAX_PROJECTION_ITERATIONS_FAILSAFE = 5000;

const cycleOptions = [
  { label: "Monthly", value: "Monthly" },
  { label: "Quaterly", value: "Quaterly" },
  { label: "Annually", value: "Annually" },
];

export default function ProjectedDepreciationModal({
  showProjectedDepreciationModal,
  setShowProjectedDepreciationModal,
}) {
  const { backendUrl } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [queryParams, setQueryParams] = useState({
    Bar_Code: showProjectedDepreciationModal.Bar_Code,
    W_ID: showProjectedDepreciationModal.Warehouse,
    Item_Name: showProjectedDepreciationModal.Item_Name,
  });

  const debouncedqueryParams = useDebounce(queryParams, 800);

  const getBatches = async debouncedqueryParams => {
    let response = await fetch(
      `${backendUrl}/api/warehouse/asset-depreciation-projection?${queryString.stringify(
        debouncedqueryParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return data;
  };

  const { data, refetch } = useQuery(
    ["DEPRECIATION_DATA", debouncedqueryParams],
    () => getBatches(debouncedqueryParams),
    {}
  );

  const deleteItem = async payload => {
    //   await waitFor(4000);
    let response = await fetch(
      `${backendUrl}/api/warehouse/delete-depreciation-projection`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(payload),
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };
  const deleteMutation = useMutation(payload => deleteItem(payload), {
    onSuccess: ({ message }) => {
      toast.success(message);
      refetch();
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  const deleteProjected = async el => {
    if (
      await ConfirmDialog({
        title: "Delete Projection",
        description: "Are you sure you want to delete?",
      })
    ) {
      deleteMutation.mutate(el);
    }
  };

  const calculateProjections = () => {
    if (!data?.depreciation || !showProjectedDepreciationModal?.UnitCost)
      return [];

    const { percentage, cycle, Start_Date = new Date() } = data.depreciation;

    const unitCost = currency(showProjectedDepreciationModal.UnitCost)
      // WAS TOLD ASSET MUST BE ONE
      .multiply(/*showProjectedDepreciationModal.Quantity*/ 1)
      .subtract(data?.amountDepreciationBeforeTheStartDate || 0).value;

    const depreciationRate = percentage / 100;

    let projections = [];
    let currentValue = unitCost;
    let serialNumber = 1;
    const startDateObj = new Date(Start_Date);

    // Set first depreciation date to end of month of start date
    let currentDate = new Date(
      startDateObj.getFullYear(),
      startDateObj.getMonth() + 1,
      0
    );

    // Continue until value is fully depreciated or reaches a reasonable limit
    let unchangedIterations = 0;

    while (
      currentValue > EPSILON &&
      serialNumber <= MAX_PROJECTION_ITERATIONS_FAILSAFE
    ) {
      let depreciationAmount;

      if (data.depreciation.type === "Percentage") {
        depreciationAmount = unitCost * depreciationRate;
      } else {
        depreciationAmount = parseFloat(data.depreciation.fixedAmount || 0);
      }

      if (!Number.isFinite(depreciationAmount)) {
        break;
      }

      // Check if this is the first depreciation period and pro-rate if needed
      if (serialNumber === 1) {
        console.log(
          `Pro-rating depreciation for item ${
            showProjectedDepreciationModal.Bar_Code
          } - ${showProjectedDepreciationModal.Item_Name} - ${
            showProjectedDepreciationModal.Warehouse
          } as the start date is within the current month.`
        );
        // GET THE DAYS IN THE MONTH using date-fns
        const daysInMonth = getDaysInMonth(startDateObj);
        const daysUsed = daysInMonth - getDate(startDateObj) + 1; // +1 to include the start date

        console.log("Days in Month", daysInMonth);
        console.log("Days Used", daysUsed);

        // pro-rate
        depreciationAmount = currency(depreciationAmount)
          .multiply(daysUsed)
          .divide(daysInMonth).value;
      }

      if (!Number.isFinite(depreciationAmount)) {
        break;
      }

      // Ensure we don't depreciate more than the remaining value
      depreciationAmount = Math.min(depreciationAmount, currentValue);

      if (depreciationAmount <= EPSILON) {
        break;
      }

      projections.push({
        sn: serialNumber,
        date: format(currentDate, "dd MMM yyyy"),
        amount: depreciationAmount,
        remainingValue: currentValue - depreciationAmount,
      });

      const nextValue = currentValue - depreciationAmount;

      if (!Number.isFinite(nextValue)) {
        break;
      }

      if (Math.abs(nextValue - currentValue) < 0.000001) {
        unchangedIterations += 1;
        if (unchangedIterations >= 2) {
          break;
        }
      } else {
        unchangedIterations = 0;
      }

      currentValue = nextValue;

      // Increment date based on cycle - always to end of period
      switch (cycle) {
        case "Monthly":
          currentDate = addMonths(currentDate, 1);
          // Set to last day of the month
          currentDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          );
          break;
        case "Quaterly":
          currentDate = addQuarters(currentDate, 1);
          // Set to last day of the quarter
          const quarterEndMonth =
            Math.ceil((currentDate.getMonth() + 1) / 3) * 3 - 1;
          currentDate = new Date(
            currentDate.getFullYear(),
            quarterEndMonth + 1,
            0
          );
          break;
        case "Annually":
          currentDate = addYears(currentDate, 1);
          // Set to last day of the year (December 31st)
          currentDate = new Date(currentDate.getFullYear(), 11, 31);
          break;
        default:
          currentDate = addMonths(currentDate, 1);
          currentDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          );
      }

      serialNumber++;
    }

    return projections;
  };

  const projections = useMemo(() => calculateProjections(), [
    data,
    showProjectedDepreciationModal,
  ]);

  const totalPages = Math.max(1, Math.ceil(projections.length / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedProjections = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * rowsPerPage;
    return projections.slice(startIndex, startIndex + rowsPerPage);
  }, [projections, safeCurrentPage, rowsPerPage]);

  const exportToExcel = () => {
    // Prepare data for Excel
    const excelData = [
      // Header row
      ["S/N", "Date", "Amount", "Net Book Value"],
      // Data rows
      ...projections.map(projection => [
        projection.sn,
        projection.date,
        projection.amount,
        projection.remainingValue,
      ]),
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projected Depreciation");

    // Generate filename
    const filename = `Projected_Depreciation_${
      showProjectedDepreciationModal?.Bar_Code
    }_${new Date().toISOString().split("T")[0]}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  return (
    <>
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        animation={false}
        enforceFocus={false}
        show={showProjectedDepreciationModal}
        onHide={() => {
          setShowProjectedDepreciationModal(false);
        }}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter h5">
            Projected Depreciation{" "}
            <Button
              onClick={() => refetch()}
              variant=""
              className="text-primary"
            >
              <CachedIcon />{" "}
            </Button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <p>
                <b>Item Code:</b> {showProjectedDepreciationModal?.Bar_Code}
              </p>
              <p>
                <b>Item Name:</b> {showProjectedDepreciationModal?.Item_Name}
              </p>
              <p>
                <b>Unit Cost:</b>{" "}
                {currency(showProjectedDepreciationModal.UnitCost, {
                  symbol: "",
                }).format()}
              </p>

              <p>
                <b>Quantity:</b> {/* WAS TOLD ASSET MUST BE ONE */}
                {currency(/*showProjectedDepreciationModal.Quantity*/ 1, {
                  symbol: "",
                  format: formatForQty,
                }).format()}
              </p>

              <p>
                <b>Total Unit Cost:</b> {/* WAS TOLD ASSET MUST BE ONE */}
                {currency(showProjectedDepreciationModal.UnitCost, {
                  symbol: "",
                })
                  .multiply(/*showProjectedDepreciationModal.Quantity*/ 1)
                  .format()}
              </p>
            </Col>{" "}
            <Col>
              <p>
                <b>Cycle:</b> {data?.depreciation?.cycle}
              </p>
              <p>
                <b>Type:</b> {data?.depreciation?.type}
              </p>

              {data?.depreciation?.type === "Percentage" ? (
                <p>
                  <b>Percentage:</b> {data?.depreciation?.percentage}%{" "}
                </p>
              ) : (
                <p>
                  <b>Fixed Amount:</b> {data?.depreciation?.fixedAmount}
                </p>
              )}

              <p>
                <b>Post Date:</b>{" "}
                {data?.depreciation
                  ? formatDate(data?.depreciation?.Post_Date)
                  : "..."}
              </p>

              <p>
                <b>Start Date:</b>{" "}
                {data?.depreciation
                  ? formatDate(data?.depreciation?.Start_Date)
                  : "..."}
              </p>
            </Col>
          </Row>

          <hr />
          <Row>
            <Col>
              <p>
                <b>Total Depreciated Value:</b>{" "}
                {currency(data?.depreciatedValue || 0, {
                  symbol: "",
                }).format()}
              </p>

              <p>
                <b>Current Value:</b> {/* WAS TOLD ASSET MUST BE ONE */}
                {currency(showProjectedDepreciationModal.UnitCost || 0, {
                  symbol: "",
                })
                  .multiply(/*showProjectedDepreciationModal.Quantity*/ 1)
                  .subtract(data?.depreciatedValue || 0)
                  .format()}
              </p>
            </Col>
            <Col>
              <p>
                <b>Projected end of life:</b>{" "}
                {projections.length > 0
                  ? projections[projections.length - 1].date
                  : "N/A"}
              </p>
              <p>
                <b>Total Periods:</b> {projections.length}
              </p>
            </Col>
          </Row>
          <hr />

          <div className="d-flex justify-content-end my-3">
            <Button
              variant=""
              title="Export"
              className="border"
              onClick={exportToExcel}
            >
              <ExcelIcon color={"green"} /> Export to Excel
            </Button>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Rows per page</span>
              <Form.Select
                size="sm"
                style={{ width: "auto" }}
                value={rowsPerPage}
                onChange={e => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
              >
                {[10, 20, 50, 100].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Form.Select>
            </div>

            {projections.length > 0 && (
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={safeCurrentPage <= 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <span className="small text-muted">
                  Page {safeCurrentPage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          <Table responsive borderless striped className="product-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Net Book Value</th>
              </tr>
            </thead>{" "}
            <tbody>
              {projections.length > 0 ? (
                paginatedProjections.map(projection => (
                  <tr key={projection.sn}>
                    <td>{projection.sn}</td>
                    <td>{projection.date}</td>
                    <td>
                      {currency(projection.amount, { symbol: "" }).format()}
                    </td>
                    <td>
                      {currency(projection.remainingValue, {
                        symbol: "",
                      }).format()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <NoTableItem />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant=""
            onClick={() => setShowProjectedDepreciationModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalLoader show={deleteMutation.isLoading} />
    </>
  );
}
